import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin, ArrowRight, ShoppingCart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import HCaptchaField from "../components/HCaptchaField";

const inputCls = "w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm";

function GoogleBtn({ label }) {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      data-testid="google-login-btn"
      onClick={handleGoogle}
      className="w-full flex items-center justify-center gap-3 border border-white/15 hover:border-white/30 text-white font-semibold py-3.5 rounded-full transition-all duration-200 hover:bg-white/5 text-sm"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {label}
    </button>
  );
}

function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/profil");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
      <GoogleBtn label="Continuer avec Google" />
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-white/30">ou par email</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Email</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input data-testid="login-email" type="email" required value={form.email}
            onChange={e => setForm(p => ({...p, email: e.target.value}))}
            placeholder="jean@email.com"
            className={`${inputCls} pl-10`} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Mot de passe</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input data-testid="login-password" type={showPass ? "text" : "password"} required value={form.password}
            onChange={e => setForm(p => ({...p, password: e.target.value}))}
            placeholder="••••••••"
            className={`${inputCls} pl-10 pr-10`} />
          <button type="button" onClick={() => setShowPass(!showPass)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
            {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
      <button data-testid="login-submit" type="submit" disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-all">
        {loading ? "Connexion…" : <><ArrowRight className="w-4 h-4" />Se connecter</>}
      </button>
    </form>
  );
}

function RegisterForm() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", password: "", confirm: "",
    telephone: "", adresse_postale: "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    if (!captchaToken) { setError("Veuillez compléter le CAPTCHA."); return; }
    if (form.password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    setLoading(true);
    try {
      await register({
        prenom: form.prenom, nom: form.nom, email: form.email,
        password: form.password, telephone: form.telephone,
        adresse_postale: form.adresse_postale || null,
        hcaptcha_token: captchaToken,
      });
      navigate("/profil");
    } catch (err) {
      setError(err.message);
      captchaRef.current?.resetCaptcha();
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  const f = (name) => ({ value: form[name], onChange: e => setForm(p => ({...p, [name]: e.target.value})) });

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
      <GoogleBtn label="S'inscrire avec Google" />
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-white/30">ou par email</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Prénom *</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input data-testid="reg-prenom" type="text" required placeholder="Jean" {...f("prenom")} className={`${inputCls} pl-10`} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Nom *</label>
          <input data-testid="reg-nom" type="text" required placeholder="Dupont" {...f("nom")} className={inputCls} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Email *</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input data-testid="reg-email" type="email" required placeholder="jean@email.com" {...f("email")} className={`${inputCls} pl-10`} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Téléphone *</label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input data-testid="reg-telephone" type="tel" required placeholder="06 12 34 56 78" {...f("telephone")} className={`${inputCls} pl-10`} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
          Adresse postale <span className="text-white/25 normal-case font-normal">(facultative — obligatoire pour envoi postal)</span>
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3 w-4 h-4 text-white/30" />
          <textarea data-testid="reg-adresse" rows={2} placeholder="9 Rue de Mont-Louis, 75011 Paris" {...f("adresse_postale")}
            className={`${inputCls} pl-10 resize-none`} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Mot de passe *</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input data-testid="reg-password" type={showPass ? "text" : "password"} required placeholder="8 car. min." {...f("password")} className={`${inputCls} pl-10 pr-10`} />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Confirmer *</label>
          <input data-testid="reg-confirm" type="password" required placeholder="••••••••" {...f("confirm")} className={inputCls} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Vérification anti-robot *</label>
        <HCaptchaField
          captchaRef={captchaRef}
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={() => setCaptchaToken("")}
        />
      </div>

      {error && <p data-testid="register-error" className="text-sm text-red-400">{error}</p>}

      <button data-testid="register-submit" type="submit" disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-all">
        {loading ? "Création du compte…" : <><ArrowRight className="w-4 h-4" />Créer mon compte</>}
      </button>
    </form>
  );
}

export default function AuthPage() {
  const [tab, setTab] = useState("login");
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { setIsOpen } = useCart();
  const navigate = useNavigate();
  const nextParam = searchParams.get("next");

  useEffect(() => {
    document.title = tab === "login" ? "Connexion — Atelier du Mac" : "Créer un compte — Atelier du Mac";
    if (searchParams.get("mode") === "register") setTab("register");
  }, [tab, searchParams]);

  useEffect(() => {
    if (user) {
      if (nextParam === "panier") {
        setIsOpen(true);
        navigate("/", { replace: true });
      } else {
        navigate("/profil", { replace: true });
      }
    }
  }, [user, navigate, nextParam, setIsOpen]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4 py-20">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-2">
            <img src="/logo-atelierdumac.png" alt="Atelier du Mac" className="h-12 w-auto object-contain" style={{ maxWidth: '200px' }} />
          </Link>
        </div>

        <div className="bg-[#111113] border border-white/10 rounded-3xl p-8">
          {/* Message si redirection depuis panier */}
          {nextParam === "panier" && (
            <div className="flex items-start gap-3 bg-[#FF5C00]/10 border border-[#FF5C00]/20 rounded-xl p-4 mb-6">
              <ShoppingCart className="w-4 h-4 text-[#FF5C00] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-white/70">
                <span className="font-semibold text-white">Connexion requise</span><br />
                Connectez-vous ou créez un compte pour finaliser votre commande.
              </p>
            </div>
          )}
          {/* Tabs */}
          <div className="flex gap-2 mb-8 p-1 bg-[#0A0A0C] rounded-full">
            <button data-testid="tab-login" onClick={() => setTab("login")}
              className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === "login" ? "bg-[#FF5C00] text-white" : "text-white/40 hover:text-white"}`}>
              Se connecter
            </button>
            <button data-testid="tab-register" onClick={() => setTab("register")}
              className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === "register" ? "bg-[#FF5C00] text-white" : "text-white/40 hover:text-white"}`}>
              Créer un compte
            </button>
          </div>

          {tab === "login" ? <LoginForm /> : <RegisterForm />}
        </div>

        <p className="text-center text-xs text-white/25 mt-6">
          {tab === "login"
            ? <>Pas encore de compte ? <button onClick={() => setTab("register")} className="text-[#FF5C00] hover:underline">S'inscrire</button></>
            : <>Déjà un compte ? <button onClick={() => setTab("login")} className="text-[#FF5C00] hover:underline">Se connecter</button></>}
        </p>
      </div>
    </div>
  );
}
